import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom"; // Added for Portal support
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import deleteIcon from "../../assets/delete-icon.png";
import editIcon from "../../assets/edit-icon.png";
import invoiceIcon from "../../assets/invoice-icon.png";

const COLORS = {
  primary: "#0D47A1",
  danger: "#D32F2F",
  profit: "#16A34A",
  bg: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E0E0E0",
  text: "#1E1E1E",
  muted: "#757575",
};

const PurchaseReturn = () => {
  const { role } = useAuth();

  const [returns, setReturns] = useState([]);
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [billProducts, setBillProducts] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const drawerRef = useRef(null);

  const initialForm = {
    bill_no: "",
    party_id: "",
    return_date: new Date().toISOString().slice(0, 10),
    items: [{ product_id: "", qty: "", rate: "" }]
  };

  const [form, setForm] = useState(initialForm);

  /* ================= LOAD DATA ================= */

  const loadData = async () => {
    try {
      const retRes = await api.get("/purchase_return/list.php");
      const productRes = await api.get("/inventory/list.php");
      const partyRes = await api.get("/parties/list.php");

      setReturns(retRes.data?.data || []);
      setProducts(productRes.data?.data || []);
      setParties(partyRes.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { loadData(); }, []);

  /* ================= FETCH BILL ================= */

  const fetchBillDetails = async (billNo) => {
    if (!billNo) return;
    try {
      const billRes = await api.get(`/purchase/get-by-bill.php?bill_no=${billNo}`);
      const data = billRes.data?.data;

      if (!data) {
        setError("Bill not found");
        return;
      }
      setBillProducts(data.items || []);
      setForm(prev => ({ ...prev, party_id: String(data.party_id) }));
    } catch (err) {
      setError("Error loading bill");
    }
  };

  /* ================= EDIT ================= */

  const handleEdit = async (r) => {
    try {
      const partyRes = await api.get("/parties/list.php");
      const partyList = partyRes.data?.data || [];
      setParties(partyList);

      const productRes = await api.get("/inventory/list.php");
      setProducts(productRes.data?.data || []);

      const billRes = await api.get(`/purchase/get-by-bill.php?bill_no=${r.bill_no}`);
      const billData = billRes.data?.data || {};
      const billItems = billData.items || [];
      setBillProducts(billItems);

      let partyId = r.party_id;
      if (!partyId && r.party_name) {
        const found = partyList.find(p => p.name === r.party_name);
        if (found) partyId = found.id;
      }

      const items = (r.items || []).map(i => {
        const billItem = billItems.find(b => String(b.product_id || b.id) === String(i.product_id));
        const rate = i.rate || billItem?.rate || billItem?.purchase_rate || 0;
        return { product_id: String(i.product_id), qty: String(i.qty), rate: String(rate) };
      });

      setForm({ bill_no: r.bill_no || "", party_id: String(partyId || ""), return_date: r.return_date || "", items });
      setEditMode(true);
      setEditId(r.id);
      setShowForm(true);
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= RESET ================= */

  const resetForm = () => {
    setShowForm(false);
    setError("");
    setBillProducts([]);
    setEditMode(false);
    setEditId(null);
    setForm(initialForm);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showForm && drawerRef.current && !drawerRef.current.contains(e.target)) {
        resetForm();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm]);

  /* ================= ITEM CHANGE ================= */

  const handleItemChange = (index, field, value) => {
    const updated = [...form.items];
    updated[index][field] = value;
    if (field === "product_id") {
      const product = billProducts.find(p => String(p.product_id) === String(value));
      if (product) updated[index].rate = product.rate;
    }
    setForm({ ...form, items: updated });
  };

  const addItemRow = () => {
    setForm({ ...form, items: [...form.items, { product_id: "", qty: "", rate: "" }] });
  };

  const removeItemRow = (index) => {
    const updated = [...form.items];
    updated.splice(index, 1);
    setForm({ ...form, items: updated });
  };

  const total = form.items.reduce((sum, item) => sum + Number(item.qty || 0) * Number(item.rate || 0), 0);

  /* ================= SUBMIT ================= */

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      id: editId,
      bill_no: form.bill_no,
      party_id: form.party_id,
      return_date: form.return_date,
      total,
      items: form.items.map(i => ({
        product_id: Number(i.product_id),
        qty: Number(i.qty),
        rate: Number(i.rate)
      }))
    };

    try {
      if (editMode) {
        await api.post("/purchase_return/update.php", payload);
      } else {
        await api.post("/purchase_return/create.php", payload);
      }
      resetForm();
      loadData();
    } catch {
      setError("Error saving return");
    }
  };

  /* ================= DELETE ================= */

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this return?")) return;
    await api.post("/purchase_return/delete.php", { id });
    loadData();
  };

  const handleReturnSlipDownload = (id) => {
    window.open(`https://codesaarthi.online/khatasaarthi/api/purchase_return/return-slip.php?id=${id}`, "_blank");
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>Purchase Return</h2>
          <p style={styles.subtitle}>Manage returned purchases</p>
        </div>
        <button style={styles.newBtn} onClick={() => setShowForm(true)}>+ Add Return</button>
      </div>

      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Bill No</th>
                <th style={styles.th}>Supplier</th>
                <th style={styles.th}>Items Returned</th>
                <th style={styles.th}>Total</th>
                <th style={styles.thCenter}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {returns.map((r, index) => (
                <tr key={r.id}>
                  <td style={styles.td}>{index + 1}</td>
                  <td style={styles.tdBold}>{r.bill_no}</td>
                  <td style={styles.td}>{r.party_name}</td>
                  <td style={styles.td}>{r.items?.map(i => `${i.product_name} (${i.qty})`).join(", ")}</td>
                  <td style={styles.td}>₹ {r.total}</td>
                  <td style={styles.actionCell}>
                    <div style={styles.iconWrapper}>
                      <button style={styles.iconBtn} onClick={() => handleReturnSlipDownload(r.id)}>
                        <img src={invoiceIcon} style={styles.icon} alt="slip" />
                      </button>
                      {role !== "SALESMAN" && (
                        <>
                          <button style={styles.iconBtn} onClick={() => handleEdit(r)}>
                            <img src={editIcon} style={styles.icon} alt="edit" />
                          </button>
                          <button style={styles.iconBtn} onClick={() => handleDelete(r.id)}>
                            <img src={deleteIcon} style={styles.icon} alt="delete" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PORTAL RENDER */}
      {showForm && createPortal(
        <div style={styles.overlay}>
          <div ref={drawerRef} style={styles.drawer}>
            <div style={styles.drawerHeader}>
              <h2 style={styles.drawerTitle}>{editMode ? "Edit Purchase Return" : "Add Purchase Return"}</h2>
              <button style={styles.closeBtn} onClick={resetForm}>✕</button>
            </div>

            <form onSubmit={submit}>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <label style={styles.label}>Bill No</label>
                  <input
                    value={form.bill_no}
                    onChange={(e) => setForm({ ...form, bill_no: e.target.value })}
                    onBlur={(e) => fetchBillDetails(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Return Date</label>
                  <input
                    type="date"
                    value={form.return_date}
                    onChange={(e) => setForm({ ...form, return_date: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={{ ...styles.field, marginBottom: 20 }}>
                  <label style={styles.label}>Supplier</label>
                  <select
                    value={form.party_id}
                    onChange={(e) => setForm({ ...form, party_id: e.target.value })}
                    style={styles.input}
                  >
                    <option value="">Select Supplier</option>
                    {parties.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                  </select>
                </div>
              </div>

              {form.items.map((item, index) => (
                <div key={index} style={styles.itemRow}>
                  <select
                    value={item.product_id}
                    onChange={(e) => handleItemChange(index, "product_id", e.target.value)}
                    style={styles.input}
                  >
                    <option value="">Select Product</option>
                    {(billProducts.length > 0 ? billProducts : products).map(p => (
                      <option key={p.product_id || p.id} value={String(p.product_id || p.id)}>
                        {p.product_name || p.name}
                        {billProducts.length > 0 ? ` (Purchased: ${p.purchased_qty ?? ""}, Sold: ${p.sold_qty ?? ""})` : ""}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.qty}
                    onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                    style={styles.input}
                  />
                  <input type="number" placeholder="Rate" value={item.rate} readOnly style={styles.input} />
                  <button type="button" onClick={() => removeItemRow(index)} style={styles.smallBtn}>Remove</button>
                </div>
              ))}

              <button type="button" onClick={addItemRow} style={styles.smallBtn}>+ Add Item</button>

              <div style={styles.summaryWrapper}>
                <div style={styles.summaryRow}>
                  <span>Total Amount</span>
                  <strong>₹ {total.toFixed(2)}</strong>
                </div>
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <div style={styles.actionRow}>
                <button type="button" style={styles.cancelBtn} onClick={resetForm}>Cancel</button>
                <button type="submit" style={styles.primaryBtn}>{editMode ? "Update Return" : "Save Return"}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const styles = {
  page: { background: COLORS.bg, padding: "10px clamp(15px,4vw,30px) 30px", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 30 },
  headerLeft: { display: "flex", flexDirection: "column" },
  title: { fontSize: 28, fontWeight: 700, marginBottom: 6 },
  subtitle: { color: COLORS.muted },
  newBtn: { background: COLORS.primary, color: "#fff", padding: "10px 20px", borderRadius: 10, border: "none", fontWeight: 600, height: 42, cursor: "pointer" },
  card: { background: COLORS.card, borderRadius: 18, padding: "25px 30px" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 850 },
  th: { padding: "14px 12px", textAlign: "left", fontWeight: 600, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  thCenter: { padding: "14px 12px", textAlign: "center", fontWeight: 600, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  td: { padding: "16px 12px", borderBottom: `1px solid ${COLORS.border}` },
  tdBold: { padding: "16px 12px", fontWeight: 600, borderBottom: `1px solid ${COLORS.border}` },
  actionCell: { borderBottom: `1px solid ${COLORS.border}`, textAlign: "center" },
  iconWrapper: { display: "flex", justifyContent: "center", gap: 16 },
  iconBtn: { background: "transparent", border: "none", cursor: "pointer" },
  icon: { width: 18, height: 18 },
  
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 9999,
  },

  drawer: {
    width: "min(600px, 100%)",
    height: "100vh", 
    background: "#fff",
    padding: "20px 30px 100px", 
    overflowY: "auto", 
    boxShadow: "-6px 0 20px rgba(0,0,0,0.15)",
    position: "relative",
    boxSizing: "border-box",
  },

  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  drawerTitle: { fontSize: 22, fontWeight: 700 },
  closeBtn: { border: "none", background: "none", fontSize: 20, cursor: "pointer" },
  grid: { display: "flex", flexDirection: "column", gap: 18 },
  field: { display: "flex", flexDirection: "column" },
  label: { marginBottom: 6, color: COLORS.muted },
  input: { width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${COLORS.border}`, boxSizing: "border-box" },
  itemRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, alignItems: "center", marginBottom: 12 },
  smallBtn: { padding: "6px 14px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: "#fff", cursor: "pointer" },
  summaryWrapper: { width: "min(260px,100%)", marginLeft: "auto", marginTop: 30, marginBottom: 30 },
  summaryRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  actionRow: { display: "flex", justifyContent: "flex-end", flexWrap: "wrap", marginTop: 30, gap: 12 },
  cancelBtn: { padding: "8px 18px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: "#fff" },
  primaryBtn: { padding: "8px 20px", borderRadius: 6, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 600 },
  error: { marginTop: 12, color: COLORS.danger }
};

export default PurchaseReturn;